import { createContext, useReducer, type ReactNode } from "react";

interface FormState {
  name: string;
  email: string;
  phone: string;
  address: string;
  phoneValid: boolean;
  phoneWarning: string;
  hasWindSpeed: boolean;
  windSpeed: string;
  isAnchored: boolean;
  showMaterials: boolean;
  materialsText: string;
  materialType: string | null;
  metalColor: string;
  shingleType: string | null;
  shingleColor: string;
  shingleColorOther: string;
  shingleBrand: string;
  shingleBrandOther: string;
  formValid: boolean;
}

type FormAction =
  | { type: "SET_FIELD"; field: string; value: string | boolean | null }
  | { type: "SET_PHONE"; value: string; valid: boolean; warning?: string }
  | { type: "SET_WIND_SPEED"; hasWindSpeed: boolean; windSpeed: string }
  | { type: "SET_ANCHORED"; value: boolean }
  | { type: "TOGGLE_MATERIALS" }
  | { type: "SET_MATERIALS"; value: string }
  | { type: "SET_MATERIAL_TYPE"; value: string }
  | { type: "SET_SHINGLE_TYPE"; value: string }
  | { type: "SET_SHINGLE_COLOR"; value: string }
  | { type: "SET_SHINGLE_BRAND"; value: string }
  | { type: "VALIDATE_FORM" }
  | { type: "RESET_FORM" };

export interface FormContextValue extends FormState {
  setField: (field: string, value: string | boolean | null) => void;
  setPhone: (value: string, valid: boolean, warning?: string) => void;
  setWindSpeed: (hasWindSpeed: boolean, windSpeed?: string) => void;
  setAnchored: (value: boolean) => void;
  toggleMaterials: () => void;
  setMaterials: (value: string) => void;
  setMaterialType: (value: string) => void;
  setShingleType: (value: string) => void;
  setShingleColor: (value: string) => void;
  setShingleBrand: (value: string) => void;
  validateForm: () => void;
  resetForm: () => void;
}

const FormContext = createContext<FormContextValue | null>(null);

const initialState: FormState = {
  name: "",
  email: "",
  phone: "",
  address: "",
  phoneValid: true,
  phoneWarning: "",
  hasWindSpeed: false,
  windSpeed: "",
  isAnchored: false,
  showMaterials: false,
  materialsText: "",
  materialType: null,
  metalColor: "",
  shingleType: null,
  shingleColor: "",
  shingleColorOther: "",
  shingleBrand: "",
  shingleBrandOther: "",
  formValid: false,
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };

    case "SET_PHONE":
      return {
        ...state,
        phone: action.value,
        phoneValid: action.valid,
        phoneWarning: action.warning || "",
      };

    case "SET_WIND_SPEED":
      return {
        ...state,
        hasWindSpeed: action.hasWindSpeed,
        windSpeed: action.hasWindSpeed ? action.windSpeed : "",
      };

    case "SET_ANCHORED":
      return { ...state, isAnchored: action.value };

    case "TOGGLE_MATERIALS":
      return { ...state, showMaterials: !state.showMaterials };

    case "SET_MATERIALS": {
      return { ...state, materialsText: action.value };
    }

    case "SET_MATERIAL_TYPE":
      return {
        ...state,
        materialType: action.value,
        metalColor: action.value === "metal" ? state.metalColor : "",
        shingleType: action.value === "shingles" ? state.shingleType : null,
        shingleColor: action.value === "shingles" ? state.shingleColor : "",
      };

    case "SET_SHINGLE_TYPE":
      return {
        ...state,
        shingleType: action.value,
        shingleColor: "",
        shingleColorOther: "",
      };

    case "SET_SHINGLE_COLOR":
      return {
        ...state,
        shingleColor: action.value,
        shingleColorOther:
          action.value === "other" ? state.shingleColorOther : "",
      };

    case "SET_SHINGLE_BRAND":
      return {
        ...state,
        shingleBrand: action.value,
        shingleBrandOther:
          action.value === "other" ? state.shingleBrandOther : "",
      };

    case "VALIDATE_FORM": {
      const isValid =
        state.name.trim() !== "" &&
        state.email.trim() !== "" &&
        state.phone.trim() !== "" &&
        state.phoneValid &&
        state.address.trim() !== "";
      return { ...state, formValid: isValid };
    }

    case "RESET_FORM":
      return initialState;

    default:
      return state;
  }
}

export function FormProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const setField = (field: string, value: string | boolean | null) => {
    dispatch({ type: "SET_FIELD", field, value });
  };

  const setPhone = (value: string, valid: boolean, warning?: string) => {
    dispatch({ type: "SET_PHONE", value, valid, warning });
  };

  const setWindSpeed = (hasWindSpeed: boolean, windSpeed = "") => {
    dispatch({ type: "SET_WIND_SPEED", hasWindSpeed, windSpeed });
  };

  const setAnchored = (value: boolean) => {
    dispatch({ type: "SET_ANCHORED", value });
  };

  const toggleMaterials = () => {
    dispatch({ type: "TOGGLE_MATERIALS" });
  };

  const setMaterials = (value: string) => {
    dispatch({ type: "SET_MATERIALS", value });
  };

  const setMaterialType = (value: string) => {
    dispatch({ type: "SET_MATERIAL_TYPE", value });
  };

  const setShingleType = (value: string) => {
    dispatch({ type: "SET_SHINGLE_TYPE", value });
  };

  const setShingleColor = (value: string) => {
    dispatch({ type: "SET_SHINGLE_COLOR", value });
  };

  const setShingleBrand = (value: string) => {
    dispatch({ type: "SET_SHINGLE_BRAND", value });
  };

  const validateForm = () => {
    dispatch({ type: "VALIDATE_FORM" });
  };

  const resetForm = () => {
    dispatch({ type: "RESET_FORM" });
  };

  const value: FormContextValue = {
    ...state,
    setField,
    setPhone,
    setWindSpeed,
    setAnchored,
    toggleMaterials,
    setMaterials,
    setMaterialType,
    setShingleType,
    setShingleColor,
    setShingleBrand,
    validateForm,
    resetForm,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export default FormContext;
